import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
// eslint-disable-next-line import/order
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import { Address, Organization, Rider, StateService, State as UzurvState } from '@uzurv/clara';
import { isNullOrUndefined, PhoneNumberUtil, UserValidators } from '@uzurv/core';
import { cloneDeep } from 'lodash';
import { OrganizationRiderState } from '../../../store/organization-rider/organization-rider.reducer';
import { Subject, map } from 'rxjs';
import { UpdateUzurvRider } from '../../../store/organization-rider/organization-rider.actions';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';

export interface EditRiderDialogData {
  uzurvRider: Rider;
  organizationRiderAddress: Address;
}

@Component({
  selector: 'app-edit-rider-dialog',
  templateUrl: './edit-rider-dialog.component.html',
  styleUrls: ['./edit-rider-dialog.component.scss']
})
export class EditRiderDialogComponent implements OnInit, OnDestroy {
  @ViewChild('editTransitProgramRiderForm', { static: true }) editTransitProgramRiderForm: NgForm;

  editableAddress: Address = new Address();
  editableUzurvRider = new Rider();
  formGroup: FormGroup;
  invalidPhoneNumber: boolean;
  organizations: Array<Organization>;
  private _destroyed = new Subject<boolean>();
  selectedOrganization: Organization;
  shouldShowDeclineTooltip: boolean;
  states: Array<UzurvState> = [];

  constructor(
    private dialogRef: MatDialogRef<EditRiderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditRiderDialogData,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private store$: Store<State<OrganizationRiderState>>,
    private stateService: StateService
  ) {}

  ngOnDestroy(): void {
    this._destroyed.next(true);
    this._destroyed.complete();
  }

  ngOnInit() {
    this.editableUzurvRider = cloneDeep(this.data.uzurvRider);
    this.editableAddress = cloneDeep(this.data.organizationRiderAddress);
    this.stateService
      .getStates()
      .pipe(
        map(states => {
          this.states = states;
        })
      )
      .subscribe();

    if (!isNullOrUndefined(this.editableUzurvRider.phoneNumber)) {
      this.invalidPhoneNumber = !PhoneNumberUtil.isValidPhoneNumber(
        this.editableUzurvRider.phoneNumber,
        PhoneNumberUtil.getCountryCode(this.editableUzurvRider.phoneNumber)
      );
    }

    this.formGroup = this.formBuilder.group({
      firstName: [this.editableUzurvRider?.firstName, Validators.required],
      lastName: [this.editableUzurvRider?.lastName, Validators.required],
      email: [
        { value: this.editableUzurvRider?.emailAddress, disabled: !isNullOrUndefined(this.editableUzurvRider.emailAddress) ? true : false },
        Validators.email
      ],
      phoneNumber: [this.editableUzurvRider?.phoneNumber, Validators.compose([UserValidators.validatePhoneNumber, Validators.required])],
      smsEnabledPhone: [this.editableUzurvRider?.smsEnabledPhone],
      streetAddress: [this.editableAddress?.streetAddress1],
      streetAddressTwo: [this.editableAddress?.streetAddress2],
      city: [this.editableAddress?.city],
      state: [this.editableAddress?.stateCode],
      zipcode: [this.editableAddress?.zipCode, Validators.pattern('^(?:\\d{5}(?:-\\d{4})?|\\d{9})$')],

      dateOfBirth: [
        this.editableUzurvRider?.dateOfBirth,
        isNullOrUndefined(this.editableUzurvRider?.dateOfBirth) ? null : Validators.required
      ]
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save() {
    // Format the number before validating.
    const formattedPhone = PhoneNumberUtil.formatPhoneNumberForRest(
      this.formGroup.get('phoneNumber').value,
      PhoneNumberUtil.getCountryCode(this.formGroup.get('phoneNumber').value)
    );
    if (!PhoneNumberUtil.isValidPhoneNumber(formattedPhone, PhoneNumberUtil.getCountryCode(formattedPhone))) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Error',
          message: 'Please enter a valid phone number'
        }
      });
      return;
    }
    this.formGroup.get('email')?.value?.trim().length == 0 ? this.formGroup.get('email').setValue(null) : null;
    this.editableUzurvRider.phoneNumber = formattedPhone;
    this.updateUzurvRider();
    this.isAnyAddressFieldFilled() ? this.updateAddress() : null;
    this.store$.dispatch(
      UpdateUzurvRider({
        payload: {
          rider: this.editableUzurvRider,
          address: this.editableAddress
        }
      })
    );
    this.dialogRef.close();
  }

  isAnyAddressFieldFilled(): boolean {
    const fieldsToCheck = ['streetAddress', 'streetAddressTwo', 'city', 'state', 'zipcode'];
    return fieldsToCheck.some(fieldName => {
      return this.formGroup?.get(fieldName)?.value?.trim().length > 0;
    });
  }

  updateUzurvRider(): void {
    this.editableUzurvRider.firstName = this.formGroup?.get('firstName')?.value;
    this.editableUzurvRider.lastName = this.formGroup?.get('lastName')?.value;
    this.editableUzurvRider.emailAddress = this.formGroup?.get('email')?.value;
    this.editableUzurvRider.smsEnabledPhone = this.formGroup?.get('smsEnabledPhone')?.value;
    this.editableUzurvRider.dateOfBirth = this.formGroup?.get('dateOfBirth')?.value;
  }

  updateAddress(): void {
    if (isNullOrUndefined(this.editableAddress)) {
      this.editableAddress = new Address();
    }
    const valueOrNull = (value: string | null): string | null => {
      const trimmedValue = value?.trim();
      return trimmedValue === '' ? null : trimmedValue;
    };

    this.editableAddress.streetAddress1 = valueOrNull(this.formGroup.get('streetAddress')?.value);
    this.editableAddress.streetAddress2 = valueOrNull(this.formGroup.get('streetAddressTwo')?.value);
    this.editableAddress.city = valueOrNull(this.formGroup.get('city')?.value);
    this.editableAddress.stateCode = valueOrNull(this.formGroup.get('state')?.value);
    this.editableAddress.zipCode = valueOrNull(this.formGroup.get('zipcode')?.value);
    this.editableAddress.personId = this.editableUzurvRider?.id;
  }
}
